import React, { useState, useEffect } from 'react';
import { formatDistanceToNow } from 'date-fns';
import {
  Box,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Text,
  Badge,
  VStack,
  Link,
  Divider,
  Flex,
  HStack,
  Input,
  FormControl,
  FormLabel,
  Tooltip,
  Icon
} from '@chakra-ui/react';
import { StrategyData, UserTransaction } from '../types/strategy';
import { InfoIcon } from '@chakra-ui/icons';
import { getChainColor } from '../utils/chainColors';

interface StrategyCardProps {
  strategyData: StrategyData;
}

const StrategyCard: React.FC<StrategyCardProps> = ({ strategyData }) => {
  const { strategy, historicalBalances } = strategyData;
  const [inflow, setInflow] = useState<string>('');
  const [autoCalculatedFlow, setAutoCalculatedFlow] = useState<string>('');
  const [isUsingAutoFlow, setIsUsingAutoFlow] = useState<boolean>(true);
  
  // Get the last balance record from historical data (first element is the latest)
  const lastBalance = historicalBalances && historicalBalances.length > 0 
    ? historicalBalances[0] 
    : null;

  // Calculate flow from userTransactions
  useEffect(() => {
    if (lastBalance && lastBalance.userTransactions && lastBalance.userTransactions.length > 0) {
      let totalFlow = 0;
      
      // Get the target time for previous day 8 AM UTC
      const targetTime = new Date();
      targetTime.setUTCDate(targetTime.getUTCDate() - 1);
      targetTime.setUTCHours(8, 0, 0, 0);
      const targetTimestamp = targetTime.getTime();
      
      // Calculate flow from transactions since the target time
      lastBalance.userTransactions.forEach((transaction: UserTransaction) => {
        const transactionTime = new Date(transaction.timestamp).getTime();
        if (transactionTime >= targetTimestamp) {
          const amount = parseFloat(transaction.amount);
          if (transaction.type === 'deposit') {
            totalFlow += amount;
          } else if (transaction.type === 'withdrawal') {
            totalFlow -= amount;
          }
        }
      });
      
      setAutoCalculatedFlow(totalFlow.toFixed(2));
      
      // Auto-populate the inflow field if using auto flow
      if (isUsingAutoFlow) {
        setInflow(totalFlow.toFixed(2));
      }
    } else {
      setAutoCalculatedFlow('0.00');
      if (isUsingAutoFlow) {
        setInflow('0.00');
      }
    }
  }, [lastBalance, isUsingAutoFlow]);
  
  // Check if we have balance data
  const hasBalanceData = !!lastBalance;
  
  // Convert balance from USDC units (6 decimals) to USDC
  const balanceInUsdc = hasBalanceData ? parseFloat(lastBalance!.balance) : 0;
  
  // Format the balance with appropriate precision
  const formattedBalance = balanceInUsdc.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  
  // Format the last updated time
  const lastUpdatedDate = hasBalanceData ? new Date(lastBalance!.timestamp) : new Date();
  const timeAgo = formatDistanceToNow(lastUpdatedDate, { addSuffix: true });

  // Format the timestamp for display
  const formattedTimestamp = lastUpdatedDate.toLocaleString();

  // Handle manual flow input
  const handleFlowChange = (value: string) => {
    setInflow(value);
    setIsUsingAutoFlow(false);
  };

  // Handle auto flow toggle
  const handleAutoFlowToggle = () => {
    setIsUsingAutoFlow(!isUsingAutoFlow);
    if (!isUsingAutoFlow) {
      setInflow(autoCalculatedFlow);
    }
  };

  return (
    <Card
      variant="outline"
      bg="white"
      _hover={{
        transform: 'translateY(-2px)',
        shadow: 'lg',
        transition: 'all 0.3s ease'
      }}
      transition="all 0.3s ease"
    >
      <CardHeader pb={2}>
        <VStack spacing={2} align="stretch">
          <Heading size="lg" color="gray.700" textAlign="center">
            {strategy.name}
          </Heading>
          <Flex justify="center">
            <Badge
              colorScheme={getChainColor(strategy.chain.name)}
              variant="solid"
              borderRadius="full"
              px={4}
              py={2}
              fontSize="sm"
            >
              {strategy.chain.name}
            </Badge>
          </Flex>
        </VStack>
      </CardHeader>
      
      <CardBody pt={0}>
        <VStack spacing={4} align="stretch">
          {/* Balance Section */}
          <Box>
            <Flex justify="space-between" align="center" py={2}>
              <Text fontSize="sm" fontWeight="medium" color="gray.600">
                Current Balance:
              </Text>
              {hasBalanceData ? (
                <Text fontSize="lg" fontWeight="semibold" color="green.500">
                  ${formattedBalance} USDC
                </Text>
              ) : (
                <Text fontSize="sm" color="gray.500">
                  No data available
                </Text>
              )}
            </Flex>
            <Divider />
          </Box>
          
          {/* Flow Input Section */}
          <Box>
            <FormControl>
              <FormLabel fontSize="sm" fontWeight="medium" color="gray.600" mb={2}>
                <HStack spacing={2}>
                  <Text>Flow (USDC):</Text>
                  {hasBalanceData && (
                    <Tooltip 
                      label={isUsingAutoFlow 
                        ? "Auto-calculated from deposits/withdrawals since yesterday 8 AM UTC" 
                        : "Manual input mode - click to switch to auto-calculated"
                      }
                      placement="top"
                    >
                      <Icon 
                        as={InfoIcon} 
                        color={isUsingAutoFlow ? "green.500" : "gray.400"}
                        cursor="pointer"
                        onClick={handleAutoFlowToggle}
                        _hover={{ color: "blue.500" }}
                      />
                    </Tooltip>
                  )}
                </HStack>
              </FormLabel>
              <Input
                type="number"
                placeholder="Enter in/out flow amount..."
                value={inflow}
                onChange={(e) => handleFlowChange(e.target.value)}
                size="sm"
                borderRadius="md"
                borderColor={isUsingAutoFlow && hasBalanceData ? "green.300" : "gray.300"}
                color="gray.800"
                bg={isUsingAutoFlow && hasBalanceData ? "green.50" : "white"}
                _placeholder={{
                  color: 'gray.400'
                }}
                _focus={{
                  borderColor: isUsingAutoFlow && hasBalanceData ? 'green.500' : 'blue.500',
                  boxShadow: isUsingAutoFlow && hasBalanceData
                    ? '0 0 0 1px var(--chakra-colors-green-500)' 
                    : '0 0 0 1px var(--chakra-colors-blue-500)'
                }}
                isDisabled={!hasBalanceData}
              />
              {isUsingAutoFlow && hasBalanceData && (
                <Text fontSize="xs" color="green.600" mt={1}>
                  Auto-calculated from transactions
                </Text>
              )}
              {!hasBalanceData && (
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Flow input disabled - no balance data available
                </Text>
              )}
            </FormControl>
            <Divider mt={3} />
          </Box>
          
          {/* Balance Change Section */}
          {(() => {
            const targetTime = new Date();
            targetTime.setUTCDate(targetTime.getUTCDate() - 1);
            targetTime.setUTCHours(8, 0, 0, 0);
            const targetTimestamp = targetTime.getTime();
            
            // Find data from previous day at 8 AM UTC (±5 minutes)
            const toleranceMs = 5 * 60 * 1000;
            let previousDayData = null;
            let minDiff = Infinity;
            
            for (const balance of historicalBalances) {
              const diff = Math.abs(balance.timestamp - targetTimestamp);
              if (diff <= toleranceMs && diff < minDiff) {
                minDiff = diff;
                previousDayData = balance;
              }
            }
            
            if (previousDayData && hasBalanceData) {
              const previousBalance = parseFloat(previousDayData.balance);
              const currentBalance = parseFloat(lastBalance!.balance);
              const inflowAmount = parseFloat(inflow) || 0;
              
              // Calculate change excluding inflow (organic growth)
              const organicChange = currentBalance - previousBalance - inflowAmount;
              const totalChange = currentBalance - previousBalance;
              
              const organicChangePercentage = previousBalance > 0 ? (organicChange / previousBalance) * 100 : 0;
              const totalChangePercentage = previousBalance > 0 ? (totalChange / previousBalance) * 100 : 0;
              
              const organicChangeFormatted = organicChange >= 0 ? `+${organicChange.toFixed(2)}` : organicChange.toFixed(2);
              const totalChangeFormatted = totalChange >= 0 ? `+${totalChange.toFixed(2)}` : totalChange.toFixed(2);
              const organicPercentageFormatted = organicChangePercentage >= 0 ? `+${organicChangePercentage.toFixed(2)}%` : `${organicChangePercentage.toFixed(2)}%`;
              const totalPercentageFormatted = totalChangePercentage >= 0 ? `+${totalChangePercentage.toFixed(2)}%` : `${totalChangePercentage.toFixed(2)}%`;
              
              // Calculate APY based on organic change
              const apy = previousBalance > 0 ? (Math.pow(1 + (organicChange / previousBalance), 365) - 1) * 100 : 0;
              const apyFormatted = apy.toFixed(2);
              
              return (
                <Box>
                  <Flex justify="space-between" align="center" py={2}>
                    <Text fontSize="sm" fontWeight="medium" color="gray.600">
                      Change (24h):
                    </Text>
                    <VStack align="end" spacing={1}>
                      <Text fontSize="sm" fontWeight="semibold" color={organicChange >= 0 ? 'green.500' : 'red.500'}>
                        {organicChangeFormatted} USDC
                      </Text>
                      <HStack spacing={2} align="center">
                        <Text fontSize="xs" color={organicChangePercentage >= 0 ? 'green.500' : 'red.500'}>
                          {organicPercentageFormatted}
                        </Text>
                        <Text fontSize="xs" color="blue.500" fontWeight="medium">
                          APY: {apyFormatted}%
                        </Text>
                      </HStack>
                      {inflowAmount !== 0 && (
                        <VStack align="end" spacing={0}>
                          <Text fontSize="xs" color="gray.500">
                            Total: {totalChangeFormatted} USDC
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            ({totalPercentageFormatted})
                          </Text>
                        </VStack>
                      )}
                    </VStack>
                  </Flex>
                  <Divider />
                </Box>
              );
            }
            
            return (
              <Box>
                <Flex justify="space-between" align="center" py={2}>
                  <Text fontSize="sm" fontWeight="medium" color="gray.600">
                    Change (24h):
                  </Text>
                  <Text fontSize="xs" color="yellow.600">
                    No data available
                  </Text>
                </Flex>
                <Divider />
              </Box>
            );
          })()}
          
          {/* Last Updated Section */}
          <Box>
            <Flex justify="space-between" align="center" py={2}>
              <Text fontSize="sm" fontWeight="medium" color="gray.600">
                Last Updated:
              </Text>
              {hasBalanceData ? (
                <VStack align="end" spacing={1}>
                  <Text fontSize="sm" color="gray.600">
                    {timeAgo}
                  </Text>
                  <Text fontSize="xs" color="gray.400">
                    {formattedTimestamp}
                  </Text>
                </VStack>
              ) : (
                <Text fontSize="sm" color="gray.500">
                  No data available
                </Text>
              )}
            </Flex>
            <Divider />
          </Box>
          
          {/* Strategy Details */}
          <VStack spacing={3} align="stretch">
            
            {/* Strategy Metadata */}
            <Box>
              <Text fontSize="xs" color="gray.500" mb={2}>
                Strategy ID: {strategy.id}
              </Text>
              <Text fontSize="xs" color="gray.500" mb={2}>
                Contract: {strategy.address.slice(0, 6)}...{strategy.address.slice(-4)}
              </Text>
              <Text fontSize="xs" color="gray.500">
                ABI Key: {strategy.abiKey}
              </Text>
            </Box>
            
            {/* Action Links */}
            <HStack spacing={3} justify="center">
              <Link
                href={strategy.website}
                isExternal
                color="blue.500"
                fontWeight="medium"
                fontSize="sm"
                _hover={{
                  color: 'blue.600',
                  textDecoration: 'underline'
                }}
              >
                View Strategy →
              </Link>
              <Link
                href={`${strategy.chain.blockExplorer}/address/${strategy.address}`}
                isExternal
                color="purple.500"
                fontWeight="medium"
                fontSize="sm"
                _hover={{
                  color: 'purple.600',
                  textDecoration: 'underline'
                }}
              >
                View on {strategy.chain.name} →
              </Link>
            </HStack>
          </VStack>
        </VStack>
      </CardBody>
    </Card>
  );
};

export default StrategyCard;
